/**
 * V7-A (A4) — „Große Sicht": der Markt im Zusammenhang.
 *
 * Zeigt EINE Lage-Zeile (Murphy-Intermarket: Dollar/Gold/Öl/Kupfer/Zinsen/
 * High-Yield im Konsens) + Ton; die einzelnen Treiber stehen einklappbar
 * darunter. Kontext-Schicht — bewegt (noch) keinen Score.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Globe } from 'lucide-react';

import { fetchIntermarketContext } from '@/lib/intermarket-read';
import { StatusDot, type StatusDotTone } from '@/components/ui/StatusDot';

const TONE: Record<string, StatusDotTone> = {
  risk_on: 'green',
  risk_off: 'red',
  inflation: 'yellow',
  mixed: 'yellow',
  unknown: 'gray',
};

function arrow(trend: string): string {
  return trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→';
}

export function IntermarketCard() {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['intermarket-context'],
    queryFn: fetchIntermarketContext,
    staleTime: 30 * 60_000,
  });
  const regime = data?.regime;
  if (!regime || regime.tone === 'unknown') return null;

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
      <div className="flex items-start gap-2">
        <Globe className="size-4 text-zinc-400 mt-0.5 shrink-0" />
        <StatusDot tone={TONE[regime.tone] ?? 'gray'} className="mt-1.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">
            Große Sicht — der Markt im Zusammenhang
          </div>
          <div className="text-sm font-semibold text-zinc-100 leading-snug">{regime.label}</div>
          <div className="text-[12px] text-zinc-400 leading-snug">{regime.satz}</div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-0.5 shrink-0 focus-ring"
        >
          Treiber
          {open ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
        </button>
      </div>
      {open && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 border-t border-zinc-800 pt-2">
          {regime.drivers.map((d) => (
            <div key={d.key} className="text-[11px] leading-snug">
              <span className="text-zinc-300 font-medium">
                {arrow(d.trend)} {d.label}
              </span>
              <span className="text-zinc-500"> — {d.note}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
